import React, { useMemo } from 'react';
import classNames from 'classnames';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { Document } from '@contentful/rich-text-types';
import { RichText } from '@/components';
import { RichTextHeading } from '@/components/common/RichTextHeading';
import { getMediaComponent } from '@/components/common/LandingPageHero/utils';
import { TypeAgeBasedHeroFields } from '@/core/generated-types';

const AgeBasedHero = ({
  heading,
  subHeading,
  description,
  heroMobileAsset,
  heroDesktopAsset,
  titleColor,
}: TypeAgeBasedHeroFields) => {
  const isDesktop = useIsDesktop();
  const mobileComponent = useMemo(
    () =>
      getMediaComponent({
        media: heroMobileAsset,
        classes: 'w-full h-full',
        autoplay: true,
      }),
    [heroMobileAsset],
  );
  const desktopComponent = useMemo(
    () =>
      getMediaComponent({
        media: heroDesktopAsset,
        classes: 'w-full h-full',
        autoplay: true,
      }),
    [heroDesktopAsset],
  );

  const asset = isDesktop ? desktopComponent : mobileComponent;
  const assetKey = isDesktop ? heroDesktopAsset.sys.id : heroMobileAsset.sys.id;
  const titleColorClass = titleColor ? `text-${titleColor}` : '';

  return (
    <div className="flex w-full flex-col lg:flex-row">
      <div className="h-[211px] md:h-[550px] lg:h-[720px] lg:w-1/2 lg:min-w-[720px]">
        {asset ? React.cloneElement(asset, { key: assetKey }) : null}
      </div>
      <div className="bg-off-white w-full p-6 pb-6 pr-[23px] lg:pl-[4%] lg:flex lg:flex-col lg:justify-center lg:w-1/2">
        <h1 className="font-bold uppercase text-base tracking-[0.96px] lg:tracking-[1.6px] lg:text-xl">
          {subHeading}
        </h1>

        <div className="max-w-[510px] my-4 lg:mt-[18px]">
          <RichTextHeading
            classes={classNames(
              `!mb-0 !md:mb-0 mt-[4px] text-[32px] leading-[40px] lg:text-5xl lg:leading-[56px] ${titleColorClass}`,
            )}
            italicClass="text-[35px] lg:text-[51px]"
            content={heading as Document}
          />
        </div>

        <p
          className="max-w-[520px] mt-1 leading-[26px] text-xl lg:mt-6
          lg:text-2xl"
        >
          <RichText content={description as Document} />
        </p>
      </div>
    </div>
  );
};

export default React.memo(AgeBasedHero);
